import React from 'react';
import styled from 'styled-components';
import {
  LayoutDashboard,
  Store,
  Users,
  ShieldCheck,
  BadgeCheck,
  FileDiff,
  ListChecks,
  Tags,
  ConciergeBell,
  Wallet,
  SearchX,
  CreditCard,
  Receipt,
  BarChart3,
  ScrollText,
  UserCog,
  KeyRound,
  LogOut,
  MapPin,
  Newspaper,
  BookMarked,
  Handshake,
  Sparkles,
  Send,
  TrendingUp,
  Star,
  Target,
  PartyPopper,
  Gift,
  Compass,
  Megaphone,
} from 'lucide-react';
import logoNeg from '../assets/logo_negative_mix.png';
import {
  BACKOFFICE_NAV_GROUP_ORDER,
  BackofficeNavGroup,
  BackofficeNavItem,
  BackofficeSectionId,
} from '../utils/access';

const ICONS: Record<BackofficeSectionId, React.ElementType> = {
  dashboard: LayoutDashboard,
  merchants: Store,
  features: Star,
  customers: Users,
  moderation: ShieldCheck,
  'experience-moderation': PartyPopper,
  claims: BadgeCheck,
  'profile-edits': FileDiff,
  'promotion-rules': ListChecks,
  'benefit-types': Gift,
  leads: Handshake,
  messaging: Send,
  localities: MapPin,
  feeds: Newspaper,
  trails: Compass,
  collections: BookMarked,
  categories: Tags,
  amenities: ConciergeBell,
  'payment-methods': Wallet,
  plans: Sparkles,
  subscriptions: CreditCard,
  billing: Receipt,
  influencers: Megaphone,
  reports: BarChart3,
  'merchant-interest': TrendingUp,
  'search-terms': SearchX,
  opportunities: Target,
  audit: ScrollText,
  employees: UserCog,
  'access-profiles': KeyRound,
};

const Shell = styled.div`
  display: flex;
  height: 100vh;
  background: ${(p) => p.theme.background};
`;

const Sidebar = styled.aside`
  width: 264px;
  flex-shrink: 0;
  background: ${(p) => p.theme.navy};
  display: flex;
  flex-direction: column;
`;

const Brand = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid ${(p) => p.theme.navySoft};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;

  img.logo {
    width: 100%;
    max-width: 200px;
    height: auto;
    /* the asset is a white/orange lockup on black; screen drops the black into the navy */
    mix-blend-mode: screen;
  }

  .tag {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${(p) => p.theme.primary};
  }
`;

const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
`;

const Group = styled.div`
  margin-bottom: 1.1rem;

  .title {
    padding: 0 1.25rem;
    margin-bottom: 0.35rem;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Item = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1.25rem;
  border: none;
  background: ${(p) => (p.$active ? p.theme.navySoft : 'transparent')};
  border-left: 3px solid ${(p) => (p.$active ? p.theme.primary : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : 'rgba(255,255,255,0.72)')};
  font-size: 0.88rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  /* botao nasce com text-align:center; sem isto um rotulo que quebra em duas
     linhas ("Moderacao de experiencias") sai centralizado no meio do menu,
     desalinhado de todos os vizinhos que cabem em uma linha so. */
  text-align: left;
  line-height: 1.25;
  cursor: pointer;
  transition: all 0.15s ease;

  /* o icone nao encolhe para o texto caber: quem cede espaco e o rotulo */
  > svg {
    flex-shrink: 0;
  }

  .label {
    flex: 1;
    /* quebra por palavra inteira; nada de "experienci-as" no meio */
    overflow-wrap: break-word;
  }

  &:hover {
    background: ${(p) => p.theme.navySoft};
    color: #fff;
  }
`;

const Footer = styled.div`
  border-top: 1px solid ${(p) => p.theme.navySoft};
  padding: 0.75rem 1rem;
  /* nome do usuario e o botao de sair na mesma linha */
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .user {
    flex: 1;
    min-width: 0;
    padding: 0.5rem 0.25rem;
    display: flex;
    flex-direction: column;
    strong {
      font-size: 0.85rem;
      color: #fff;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    small {
      font-size: 0.72rem;
      color: rgba(255, 255, 255, 0.5);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

/**
 * Sair compacto: so o icone, a direita do nome do usuario (mesma linha),
 * para o rodape do sidemenu nao ocupar duas linhas.
 */
const LogoutButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  width: 34px;
  border: 1px solid ${(p) => p.theme.navySoft};
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.navySoft};
    color: #fff;
  }
`;

const Main = styled.main`
  flex: 1;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 1.5rem;
`;

interface BackofficeLayoutProps {
  activeSection: BackofficeSectionId;
  onSectionChange: (section: BackofficeSectionId) => void;
  onLogout: () => void;
  userName?: string;
  navigationItems: BackofficeNavItem[];
  children: React.ReactNode;
}

const BackofficeLayout: React.FC<BackofficeLayoutProps> = ({
  activeSection,
  onSectionChange,
  onLogout,
  userName,
  navigationItems,
  children,
}) => {
  const groups = BACKOFFICE_NAV_GROUP_ORDER.map((group: BackofficeNavGroup) => ({
    group,
    items: navigationItems.filter((item) => item.group === group),
  })).filter((entry) => entry.items.length > 0);

  return (
    <Shell>
      <Sidebar>
        <Brand>
          <img className="logo" src={logoNeg} alt="Kardume Urbano" />
          <div className="tag">Backoffice</div>
        </Brand>
        <Nav>
          {groups.map(({ group, items }) => (
            <Group key={group}>
              <div className="title">{group}</div>
              {items.map((item) => {
                const Icon = ICONS[item.section];
                return (
                  <Item
                    key={item.section}
                    $active={activeSection === item.section}
                    onClick={() => onSectionChange(item.section)}
                  >
                    <Icon size={18} />
                    <span className="label">{item.label}</span>
                  </Item>
                );
              })}
            </Group>
          ))}
        </Nav>
        <Footer>
          <div className="user">
            <strong>{userName || 'Time Kardume'}</strong>
            <small>Acesso por perfil</small>
          </div>
          <LogoutButton type="button" onClick={onLogout} title="Sair" aria-label="Sair">
            <LogOut size={18} />
          </LogoutButton>
        </Footer>
      </Sidebar>
      <Main>
        <Content>{children}</Content>
      </Main>
    </Shell>
  );
};

export default BackofficeLayout;
